/* Saying when, in words a person uses.
 *
 * These lived in TodayScreen and were copied into the next screen that needed
 * them, which is how two views of the same day end up disagreeing about
 * whether 90 minutes is "1h 30m" or "1.5h". One definition each, here.
 *
 * The rule they all follow: a date is a machine's answer to "when is it", and
 * every screen in this app is read by a person. `2026-09-11` becomes "Friday
 * 11 September", and — when it is close enough to matter — "Tomorrow".
 */

import type { MinuteOfDay } from '@shared/types';

export const pad = (n: number) => String(n).padStart(2, '0');

/** Minutes from midnight as `HH:MM`. */
export const hhmm = (min: MinuteOfDay) =>
  `${pad(Math.floor(min / 60) % 24)}:${pad(min % 60)}`;

/** `HH:MM` back to minutes, forgiving of anything a browser might hand over. */
export const toMinuteOfDay = (value: string): MinuteOfDay => {
  const [h, m] = String(value || '').split(':').map(Number);
  return (Number.isFinite(h) ? h : 9) * 60 + (Number.isFinite(m) ? m : 0);
};

/** "45m", "2h", "1h 30m" — a duration, never a decimal. */
export function human(min: number): string {
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60), m = min % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

/** "3.5h" — for totals, where a decimal reads better than "3h 30m". */
export const hours = (min: number) => `${Math.round(min / 6) / 10}h`;

export const shiftDate = (date: string, days: number): string => {
  const d = new Date(date + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

/** "Friday 11 September", or the short form for a heading. */
export function friendlyDate(date: string, short = false): string {
  try {
    return new Intl.DateTimeFormat(undefined, short
      ? { weekday: 'long', day: 'numeric', month: 'short', timeZone: 'UTC' }
      : { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' })
      .format(new Date(date + 'T12:00:00Z'));
  } catch { return date; }
}

/** Whole days from `today` to `date`. Negative is the past. */
export const daysBetween = (date: string, today: string) =>
  Math.round((Date.parse(date + 'T12:00:00Z') - Date.parse(today + 'T12:00:00Z')) / 86_400_000);

/** "Today", "Tomorrow", "In 3 days", "2 days ago". Counting days in your head
    from two ISO dates is work the screen should have done. */
export function relativeLabel(date: string, today: string): string {
  const days = daysBetween(date, today);
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  return days > 0 ? `In ${days} days` : `${Math.abs(days)} days ago`;
}

/** The same, but `null` when the date is far enough away that a weekday name
    is more use than a countdown. */
export function nearLabel(date: string, today: string): string | null {
  const days = daysBetween(date, today);
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  if (days > 1 && days <= 6) return `In ${days} days`;
  if (days < -1 && days >= -6) return `${Math.abs(days)} days ago`;
  return null;
}

/** Minutes since local midnight, in the account's timezone — never the
    browser's. A device in another zone must not decide where "now" falls in
    somebody else's day. */
export function nowMinutesIn(tz?: string | null): MinuteOfDay {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: tz || 'UTC', hour12: false, hour: '2-digit', minute: '2-digit'
    }).formatToParts(new Date());
    const h = Number(parts.find(p => p.type === 'hour')?.value ?? 0);
    const m = Number(parts.find(p => p.type === 'minute')?.value ?? 0);
    return (h % 24) * 60 + m;
  } catch {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
  }
}
